import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Lock, User } from "lucide-react";
import axios from "axios";

const API = '/api';

export default function AccountSettings() {
  const { user, getAuthHeader, isShooter } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Change Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  
  // Change PIN state
  const [pinData, setPinData] = useState({
    currentPin: "",
    newPin: "",
    confirmPin: ""
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, { headers: getAuthHeader() });
      
      toast.success("Password changed successfully!");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();
    
    if (pinData.newPin !== pinData.confirmPin) {
      toast.error("New PINs do not match");
      return;
    }
    
    if (!/^\d{4}$/.test(pinData.newPin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    
    setLoading(true);
    try {
      await axios.put(`${API}/auth/change-pin`, {
        shooter_id: user?.shooter_id,
        current_pin: pinData.currentPin || "",
        new_pin: pinData.newPin
      }, { headers: getAuthHeader() });
      
      toast.success("PIN changed successfully!");
      setPinData({ currentPin: "", newPin: "", confirmPin: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change PIN");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="account-settings-page" className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
          Account Settings
        </h1>
        <p className="text-slate-500 mt-1">Manage your account security</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Change Password Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="text-[#1B3B6F]" size={20} />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  required
                  data-testid="current-password"
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password"
                  minLength={6}
                  required
                  data-testid="new-password"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                disabled={loading}
                data-testid="change-password-btn"
              >
                {loading ? "Changing..." : "Change Password"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change PIN Card - Only for shooters */}
        {(isShooter() || user?.shooter_id) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="text-[#1B3B6F]" size={20} />
                Change PIN
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePin} className="space-y-4">
                <div>
                  <Label htmlFor="current-pin">Current PIN (if set)</Label>
                  <Input
                    id="current-pin"
                    type="password"
                    value={pinData.currentPin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPinData({ ...pinData, currentPin: val });
                    }}
                    placeholder="Enter current PIN"
                    maxLength={4}
                    data-testid="current-pin"
                  />
                  <p className="text-xs text-slate-400 mt-1">Leave blank if you haven't set a PIN yet</p>
                </div>
                <div>
                  <Label htmlFor="new-pin">New PIN</Label>
                  <Input
                    id="new-pin"
                    type="password"
                    value={pinData.newPin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPinData({ ...pinData, newPin: val });
                    }}
                    placeholder="4-digit PIN"
                    maxLength={4}
                    required
                    data-testid="new-pin"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-pin">Confirm New PIN</Label>
                  <Input
                    id="confirm-pin"
                    type="password"
                    value={pinData.confirmPin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setPinData({ ...pinData, confirmPin: val });
                    }}
                    placeholder="Confirm PIN"
                    maxLength={4}
                    required
                    data-testid="confirm-pin"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                  disabled={loading}
                  data-testid="change-pin-btn"
                >
                  {loading ? "Changing..." : "Change PIN"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="text-[#1B3B6F]" size={20} />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-500">Name</Label>
              <p className="font-medium">{user?.name || "-"}</p>
            </div>
            <div>
              <Label className="text-slate-500">Email</Label>
              <p className="font-medium">{user?.email || "-"}</p>
            </div>
            <div>
              <Label className="text-slate-500">Role</Label>
              <p className="font-medium capitalize">{user?.role || "-"}</p>
            </div>
            {user?.shooter_id && (
              <div>
                <Label className="text-slate-500">Shooter ID</Label>
                <p className="font-medium">{user?.shooter_id}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
